// Shared utility for caching portfolio and watchlist symbols on client-side
// Updates cache when user adds/removes symbols

import { clearStocksByDateMemoryCache } from './stocksByMarketDate';

const PW_STOCK_INDEX_CACHE_KEY = 'pwStockIndexCache';
const PORTFOLIO_CACHE_KEY = 'portfolioSymbolsCache';
const WATCHLIST_CACHE_KEY = 'watchlistSymbolsCache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const PORTFOLIO_DATA_CACHE_PREFIX = 'portfolioData';
const PORTFOLIO_DATA_CACHE_DURATION = 60 * 60 * 1000; // 1 hour
const LATEST_TRADE_DATE_CACHE_KEY = 'latestTradeDateCache';
const LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY = 'latestTradeDateByMarketCache';

/**
 * Get cached portfolio map for a user
 * @param {string} userEmail - User's email
 * @returns {Object|null} Cached portfolio map or null if not found/expired
 */
export const getCachedPortfolioMap = (userEmail) => {
  if (!userEmail) return null;
  
  try {
    const cachedData = localStorage.getItem(`${PORTFOLIO_CACHE_KEY}_${userEmail}`);
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      const age = Date.now() - parsed.timestamp;
      
      if (age < CACHE_TTL) {
        console.log(`[PORTFOLIO CACHE] Using cached portfolio map for ${userEmail} (age: ${Math.round(age / 1000)}s)`);
        return parsed.portfolioMap || {};
      } else {
        console.log(`[PORTFOLIO CACHE] Cache expired for ${userEmail} (age: ${Math.round(age / 1000)}s)`);
        localStorage.removeItem(`${PORTFOLIO_CACHE_KEY}_${userEmail}`);
      }
    }
  } catch (error) {
    console.warn(`[PORTFOLIO CACHE] Error reading cache for ${userEmail}:`, error);
  }
  
  return null;
};

/**
 * Cache portfolio map for a user
 * @param {string} userEmail - User's email
 * @param {Object} portfolioMap - Portfolio map to cache
 */
export const setCachedPortfolioMap = (userEmail, portfolioMap) => {
  if (!userEmail || portfolioMap == null) return;
  
  try {
    const cacheData = {
      portfolioMap: portfolioMap,
      timestamp: Date.now(),
      userEmail: userEmail
    };
    localStorage.setItem(`${PORTFOLIO_CACHE_KEY}_${userEmail}`, JSON.stringify(cacheData));
    console.log(`[PORTFOLIO CACHE] Cached portfolio map for ${userEmail}`);
  } catch (error) {
    console.warn(`[PORTFOLIO CACHE] Error caching portfolio map for ${userEmail}:`, error);
  }
};

/**
 * Update cached portfolio map after add/remove operation
 * @param {string} userEmail - User's email
 * @param {Object} updatedPortfolioMap - Updated portfolio map from API response
 */
export const updateCachedPortfolioMap = (userEmail, updatedPortfolioMap) => {
  if (!userEmail || !updatedPortfolioMap) return;
  
  try {
    setCachedPortfolioMap(userEmail, updatedPortfolioMap);
    console.log(`[PORTFOLIO CACHE] Updated cached portfolio map for ${userEmail}`);
  } catch (error) {
    console.warn(`[PORTFOLIO CACHE] Error updating cache for ${userEmail}:`, error);
  }
};

/**
 * Get cached watchlist map for a user
 * @param {string} userEmail - User's email
 * @returns {Object|null} Cached watchlist map or null if not found/expired
 */
export const getCachedWatchlistMap = (userEmail) => {
  if (!userEmail) return null;
  
  try {
    const cachedData = localStorage.getItem(`${WATCHLIST_CACHE_KEY}_${userEmail}`);
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      const age = Date.now() - parsed.timestamp;
      
      if (age < CACHE_TTL) {
        console.log(`[WATCHLIST CACHE] Using cached watchlist map for ${userEmail} (age: ${Math.round(age / 1000)}s)`);
        return parsed.watchlistMap || {};
      } else {
        console.log(`[WATCHLIST CACHE] Cache expired for ${userEmail} (age: ${Math.round(age / 1000)}s)`);
        localStorage.removeItem(`${WATCHLIST_CACHE_KEY}_${userEmail}`);
      }
    }
  } catch (error) {
    console.warn(`[WATCHLIST CACHE] Error reading cache for ${userEmail}:`, error);
  }
  
  return null;
};

/**
 * Cache watchlist map for a user
 * @param {string} userEmail - User's email
 * @param {Object} watchlistMap - Watchlist map to cache
 */
export const setCachedWatchlistMap = (userEmail, watchlistMap) => {
  if (!userEmail || watchlistMap == null) return;
  
  try {
    const cacheData = {
      watchlistMap: watchlistMap,
      timestamp: Date.now(),
      userEmail: userEmail
    };
    localStorage.setItem(`${WATCHLIST_CACHE_KEY}_${userEmail}`, JSON.stringify(cacheData));
    console.log(`[WATCHLIST CACHE] Cached watchlist map for ${userEmail}`);
  } catch (error) {
    console.warn(`[WATCHLIST CACHE] Error caching watchlist map for ${userEmail}:`, error);
  }
};

/**
 * Update cached watchlist map after add/remove operation
 * @param {string} userEmail - User's email
 * @param {Object} updatedWatchlistMap - Updated watchlist map from API response
 */
export const updateCachedWatchlistMap = (userEmail, updatedWatchlistMap) => {
  if (!userEmail || !updatedWatchlistMap) return;
  
  try {
    setCachedWatchlistMap(userEmail, updatedWatchlistMap);
    console.log(`[WATCHLIST CACHE] Updated cached watchlist map for ${userEmail}`);
  } catch (error) {
    console.warn(`[WATCHLIST CACHE] Error updating cache for ${userEmail}:`, error);
  }
};

/**
 * Clear cached portfolio map for a user
 * @param {string} userEmail - User's email
 */
export const clearCachedPortfolioMap = (userEmail) => {
  if (!userEmail) return;
  
  try {
    localStorage.removeItem(`${PORTFOLIO_CACHE_KEY}_${userEmail}`);
    console.log(`[PORTFOLIO CACHE] Cleared cache for ${userEmail}`);
  } catch (error) {
    console.warn(`[PORTFOLIO CACHE] Error clearing cache for ${userEmail}:`, error);
  }
};

/**
 * Clear cached portfolio data (stocks/summary) for a specific portfolio
 * Similar to clearCachedWatchlistData for Watchlist component
 * @param {string} userEmail - User's email
 * @param {string} portfolioName - Portfolio name (e.g., 'portfolio_1')
 */
export const clearCachedPortfolioData = (userEmail, portfolioName) => {
  if (!userEmail || !portfolioName) return;

  const CACHE_KEY = `${PORTFOLIO_DATA_CACHE_PREFIX}_${userEmail}_${portfolioName}`;

  try {
    localStorage.removeItem(CACHE_KEY);
    console.log(`[PORTFOLIO DATA CACHE] Cleared cache for ${userEmail}/${portfolioName}`);
  } catch (error) {
    console.warn(`[PORTFOLIO DATA CACHE] Error clearing cache for ${userEmail}/${portfolioName}:`, error);
  }
};

/**
 * Read the latest trade date from localStorage synchronously (no API call).
 * Returns null if cache is missing or unreadable.
 * @returns {string|null} Latest trade date (YYYY-MM-DD) or null
 */
/** @deprecated Use getLatestTradeDateKeySync for cache guards */
export const getLatestTradeDateSync = () => {
  try {
    const raw = localStorage.getItem(LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.data) {
        const d = parsed.data;
        return `US:${d.US || ''}|CA:${d.CA || ''}`;
      }
    }
    const legacy = localStorage.getItem(LATEST_TRADE_DATE_CACHE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      if (parsed && parsed.data) return `US:${parsed.data}|CA:${parsed.data}`;
    }
  } catch { /* ignore */ }
  return null;
};

/** Stable per-market trade date key for cache invalidation (US:...|CA:...). */
export const getLatestTradeDateKeySync = getLatestTradeDateSync;

/**
 * Get cached portfolio data (stocks/summary) for a specific portfolio.
 * Validates TTL and trade date — returns null when the data was built
 * against a different trade date so callers fall through to a fresh fetch.
 * @param {string} userEmail
 * @param {string} portfolioName
 * @returns {Object|null} Cached data object or null
 */
export const getCachedPortfolioData = (userEmail, portfolioName) => {
  if (!userEmail || !portfolioName) return null;

  const CACHE_KEY = `${PORTFOLIO_DATA_CACHE_PREFIX}_${userEmail}_${portfolioName}`;

  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const cacheAge = Date.now() - (parsed.timestamp || 0);

    if (cacheAge >= PORTFOLIO_DATA_CACHE_DURATION) {
      console.log(`[PORTFOLIO DATA CACHE] Cache expired for ${portfolioName} (age: ${Math.round(cacheAge / 1000)}s)`);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    // Trade-date guard: if the cache was built against a different trade date
    // than the current latest, treat it as stale so fresh data is loaded.
    const latestTradeDate = getLatestTradeDateSync();
    if (latestTradeDate && parsed.tradeDate && parsed.tradeDate !== latestTradeDate) {
      console.log(
        `[PORTFOLIO DATA CACHE] Trade date changed (cached: ${parsed.tradeDate}, latest: ${latestTradeDate}), invalidating ${portfolioName}`
      );
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    // Legacy cache entries without tradeDate are treated as stale when we
    // know the latest trade date — forces a one-time refresh after the upgrade.
    if (latestTradeDate && !parsed.tradeDate) {
      console.log(`[PORTFOLIO DATA CACHE] Legacy cache (no tradeDate), invalidating ${portfolioName}`);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    console.log(`[PORTFOLIO DATA CACHE] Using cached data for ${portfolioName} (age: ${Math.round(cacheAge / 1000)}s, tradeDate: ${parsed.tradeDate || 'N/A'})`);
    return parsed;
  } catch (error) {
    console.warn(`[PORTFOLIO DATA CACHE] Error reading cache for ${userEmail}/${portfolioName}:`, error);
    try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
    return null;
  }
};

/**
 * Cache portfolio data (stocks/summary) together with the trade date
 * the data was computed from, so the cache can self-invalidate when
 * a new trade date becomes available.
 * @param {string} userEmail
 * @param {string} portfolioName
 * @param {Object} portfolioData - { portfolio, portfolioStocks, portfolioSummary }
 * @param {string} tradeDate - Trade date key (per-market fingerprint or legacy YYYY-MM-DD)
 */
export const setCachedPortfolioDataWithDate = (userEmail, portfolioName, portfolioData, tradeDate) => {
  if (!userEmail || !portfolioName || !portfolioData) return;

  const CACHE_KEY = `${PORTFOLIO_DATA_CACHE_PREFIX}_${userEmail}_${portfolioName}`;

  try {
    const cacheData = {
      ...portfolioData,
      timestamp: Date.now(),
      activePortfolio: portfolioName,
      tradeDate: tradeDate || null,
      userEmail: userEmail,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    console.log(`[PORTFOLIO DATA CACHE] Cached data for ${portfolioName} (tradeDate: ${tradeDate || 'N/A'})`);
  } catch (error) {
    console.warn(`[PORTFOLIO DATA CACHE] Error writing cache for ${userEmail}/${portfolioName}:`, error);
  }
};

/**
 * Invalidate all portfolioData caches for a user whose stored tradeDate
 * does not match the given latestTradeDate.  Call this on page load to
 * eagerly clear stale caches before any component tries to read them.
 * @param {string} userEmail
 * @param {string} latestTradeDateKey - Per-market trade date key from getLatestTradeDateKeySync()
 * @returns {number} Number of cache entries invalidated
 */
export const invalidatePortfolioDataOnNewTradeDate = (userEmail, latestTradeDateKey) => {
  if (!userEmail || !latestTradeDateKey) return 0;

  const prefix = `${PORTFOLIO_DATA_CACHE_PREFIX}_${userEmail}_`;
  let invalidated = 0;

  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;

      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);

        if (!parsed.tradeDate || parsed.tradeDate !== latestTradeDateKey) {
          localStorage.removeItem(key);
          invalidated++;
          console.log(`[PORTFOLIO DATA CACHE] Invalidated stale cache: ${key} (had tradeDate: ${parsed.tradeDate || 'none'})`);
        }
      } catch {
        localStorage.removeItem(key);
        invalidated++;
      }
    }
  } catch (error) {
    console.warn(`[PORTFOLIO DATA CACHE] Error during trade-date invalidation:`, error);
  }

  if (invalidated > 0) {
    console.log(`[PORTFOLIO DATA CACHE] Invalidated ${invalidated} stale cache(s) for ${userEmail} (latestTradeDateKey: ${latestTradeDateKey})`);
  }
  return invalidated;
};

/**
 * Clear cached watchlist map for a user
 * @param {string} userEmail - User's email
 */
export const clearCachedWatchlistMap = (userEmail) => {
  if (!userEmail) return;
  
  try {
    localStorage.removeItem(`${WATCHLIST_CACHE_KEY}_${userEmail}`);
    console.log(`[WATCHLIST CACHE] Cleared cache for ${userEmail}`);
  } catch (error) {
    console.warn(`[WATCHLIST CACHE] Error clearing cache for ${userEmail}:`, error);
  }
};

/**
 * Get cached portfolio data (symbols) for a specific portfolio
 * @param {string} userEmail - User's email
 * @param {string} portfolioName - Portfolio name (e.g., 'portfolio_1')
 * @returns {Object|null} Cached portfolio data or null if not found/expired
 */
export const getCachedPortfolio = (userEmail, portfolioName) => {
  if (!userEmail || !portfolioName) return null;
  
  try {
    const cachedMap = getCachedPortfolioMap(userEmail);
    if (cachedMap && cachedMap[portfolioName]) {
      return cachedMap[portfolioName];
    }
  } catch (error) {
    console.warn(`[PORTFOLIO CACHE] Error reading portfolio data for ${userEmail}/${portfolioName}:`, error);
  }
  
  return null;
};

/**
 * Get cached watchlist data (symbols) for a specific watchlist
 * @param {string} userEmail - User's email
 * @param {string} watchlistName - Watchlist name (e.g., 'watchlist_1')
 * @returns {Array|null} Cached watchlist data or null if not found/expired
 */
export const getCachedWatchlist = (userEmail, watchlistName) => {
  if (!userEmail || !watchlistName) return null;
  
  try {
    const cachedMap = getCachedWatchlistMap(userEmail);
    if (cachedMap && cachedMap[watchlistName]) {
      return cachedMap[watchlistName];
    }
  } catch (error) {
    console.warn(`[WATCHLIST CACHE] Error reading watchlist data for ${userEmail}/${watchlistName}:`, error);
  }
  
  return null;
};

/**
 * Get cached watchlist data (stocks/summary) for a specific watchlist
 * Similar to portfolioData cache for Portfolio component
 * @param {string} userEmail - User's email
 * @param {string} watchlistName - Watchlist name (e.g., 'watchlist_1')
 * @returns {Object|null} Cached watchlist data with stocks and summary or null if not found/expired
 */
export const getCachedWatchlistData = (userEmail, watchlistName) => {
  if (!userEmail || !watchlistName) return null;

  const CACHE_KEY = `watchlistData_${userEmail}_${watchlistName}`;

  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (!cachedData) return null;

    const parsed = JSON.parse(cachedData);
    const cacheAge = Date.now() - (parsed.timestamp || 0);

    if (cacheAge >= PORTFOLIO_DATA_CACHE_DURATION) {
      console.log(`[WATCHLIST DATA CACHE] Cache expired for ${watchlistName} (age: ${Math.round(cacheAge / 1000)}s)`);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    if (parsed.activeWatchlist !== watchlistName) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    const latestTradeDate = getLatestTradeDateSync();
    if (latestTradeDate && parsed.tradeDate && parsed.tradeDate !== latestTradeDate) {
      console.log(
        `[WATCHLIST DATA CACHE] Trade date changed (cached: ${parsed.tradeDate}, latest: ${latestTradeDate}), invalidating ${watchlistName}`
      );
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    if (latestTradeDate && !parsed.tradeDate) {
      console.log(`[WATCHLIST DATA CACHE] Legacy cache (no tradeDate), invalidating ${watchlistName}`);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    console.log(`[WATCHLIST DATA CACHE] Using cached watchlist data for ${watchlistName} (age: ${Math.round(cacheAge / 1000)}s, tradeDate: ${parsed.tradeDate || 'N/A'})`);
    return parsed;
  } catch (error) {
    console.warn(`[WATCHLIST DATA CACHE] Error reading cache for ${userEmail}/${watchlistName}:`, error);
    try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
  }

  return null;
};

/**
 * Cache watchlist data (stocks/summary) for a specific watchlist
 * Similar to portfolioData cache for Portfolio component
 * @param {string} userEmail - User's email
 * @param {string} watchlistName - Watchlist name (e.g., 'watchlist_1')
 * @param {Object} watchlistData - Watchlist data object with watchlist, watchlistStocks, watchlistSummary
 */
export const setCachedWatchlistData = (userEmail, watchlistName, watchlistData, tradeDate = null) => {
  setCachedWatchlistDataWithDate(userEmail, watchlistName, watchlistData, tradeDate);
};

/**
 * Cache watchlist data (stocks/summary) with per-market trade date key for invalidation.
 * @param {string} userEmail
 * @param {string} watchlistName
 * @param {Object} watchlistData
 * @param {string|null} tradeDate - US:...|CA:... fingerprint
 */
export const setCachedWatchlistDataWithDate = (userEmail, watchlistName, watchlistData, tradeDate = null) => {
  if (!userEmail || !watchlistName || !watchlistData) return;

  const CACHE_KEY = `watchlistData_${userEmail}_${watchlistName}`;

  try {
    const cacheData = {
      ...watchlistData,
      timestamp: Date.now(),
      activeWatchlist: watchlistName,
      tradeDate: tradeDate || null,
      userEmail: userEmail,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    console.log(`[WATCHLIST DATA CACHE] Cached watchlist data for ${watchlistName} (tradeDate: ${tradeDate || 'N/A'})`);
  } catch (error) {
    console.warn(`[WATCHLIST DATA CACHE] Error caching watchlist data for ${userEmail}/${watchlistName}:`, error);
  }
};

/**
 * Clear cached watchlist data for a specific watchlist
 * @param {string} userEmail - User's email
 * @param {string} watchlistName - Watchlist name (e.g., 'watchlist_1')
 */
export const clearCachedWatchlistData = (userEmail, watchlistName) => {
  if (!userEmail || !watchlistName) return;
  
  const CACHE_KEY = `watchlistData_${userEmail}_${watchlistName}`;
  
  try {
    localStorage.removeItem(CACHE_KEY);
    console.log(`[WATCHLIST DATA CACHE] Cleared cache for ${userEmail}/${watchlistName}`);
  } catch (error) {
    console.warn(`[WATCHLIST DATA CACHE] Error clearing cache for ${userEmail}/${watchlistName}:`, error);
  }
};

const removeLocalStorageByPrefix = (prefix) => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.warn(`[PORTFOLIO/WATCHLIST CACHE] Error clearing keys with prefix ${prefix}:`, error);
  }
};

export const clearPortfolioClientCaches = () => {
  removeLocalStorageByPrefix(`${PORTFOLIO_CACHE_KEY}_`);
  removeLocalStorageByPrefix(`${PORTFOLIO_DATA_CACHE_PREFIX}_`);
  console.log('[PORTFOLIO CACHE] Cleared all portfolio client caches');
};

export const clearWatchlistClientCaches = () => {
  removeLocalStorageByPrefix(`${WATCHLIST_CACHE_KEY}_`);
  removeLocalStorageByPrefix('watchlistData_');
  console.log('[WATCHLIST CACHE] Cleared all watchlist client caches');
};

export const clearAllPortfolioWatchlistClientCaches = () => {
  clearPortfolioClientCaches();
  clearWatchlistClientCaches();
  clearStocksByDateMemoryCache();
  try {
    localStorage.removeItem(PW_STOCK_INDEX_CACHE_KEY);
  } catch { /* ignore */ }
};
